// Stepper.styles.ts

import {Platform, StyleSheet} from 'react-native';
import Typography from '../../../../shared/constants/Typography';
import {screenThemes} from '../../../../shared/theme/screenThemes';

type StepperTheme = typeof screenThemes.stepper.light;

/**
 * Stepper styles — hybrid equal-column layout.
 *
 * The row contains N equal flex:1 step columns (circle + label) and N-1
 * absolutely-positioned connector lines drawn from circle edge to circle edge.
 *
 * Step columns (flex:1) give every step equal width, so circles are naturally
 * centred within their column — no manual padding or measurement required.
 * The first and last circles are inset from the container edges by half a
 * column width, matching the Figma "spacing at start and end" requirement.
 *
 * Connectors are position:'absolute' with left/width set by the component
 * from the measured row width:
 *   left  = columnWidth × (i + 0.5) + 16   (right edge of circle i)
 *   width = columnWidth − 32               (gap between consecutive circles)
 *   top   = 15                             ((32 − 2) / 2  →  centres 2 px line)
 *
 * Circles have zIndex:1 so they render on top of the connector lines even
 * though connectors are rendered first in the JSX.
 */
export const createStepperStyles = (theme: StepperTheme) =>
  StyleSheet.create({
    // ── Outer container ──────────────────────────────────────────────────────
    container: {
      backgroundColor: theme.background,
      width: Typography.fullWidth,
      minHeight: Typography.height.height86,
      paddingVertical: Typography.padding.padding16,
      paddingHorizontal: Typography.padding.padding24,
      borderTopLeftRadius: Typography.radius.radius8,
      borderTopRightRadius: Typography.radius.radius8,
      borderBottomWidth: Typography.borderWidth.borderWidth1,
      borderBottomColor: theme.stepperBorder,
      shadowColor: theme.stepperBorder,
      shadowOffset: {
        width: 0,
        height: Typography.height.height4,
      },
      shadowOpacity: 0.05,
      shadowRadius: Typography.radius.radius4,
      elevation: Typography.height.height4,
      boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.05)',
    },

    // ── Row: contains stepGroups (flex) + connectors (absolute) ──────────
    // position:'relative' is the default in RN — absolute children use this
    // View as their containing block.
    row: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      width: Typography.fullWidth,
    },

    // ── Step column: circle at top-centre + label centred below ──────────
    // flex:1 → equal width for every step; circle centred by alignItems.
    // zIndex:1 so circles appear above the absolutely-positioned connectors.
    stepGroup: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'center',
      rowGap: Typography.margin.margin8,
      zIndex: 1,
    },

    // ── Circle badge ─────────────────────────────────────────────────────────
    circle: {
      width: Typography.width.width32,
      height: Typography.height.height32,
      borderRadius: Typography.radius.fullRound,
      borderWidth: Typography.borderWidth.borderWidth0125,
      alignItems: Typography.textAlign.centerAlign,
      justifyContent: Typography.textAlign.centerAlign,
      flexShrink: 0,
    },

    circleActive: {
      backgroundColor: theme.activeCircleBG,
      borderColor: theme.activeCircleBorder,
    },

    circleCompleted: {
      backgroundColor: theme.completedCircleBG,
      borderColor: 'transparent',
    },

    circleInactive: {
      backgroundColor: theme.inactiveCircleBG,
      borderColor: theme.inactiveCircleBorder,
    },

    circleText: {
      fontSize: Typography.fontSize.fontSize12,
      fontWeight: Typography.fontWeight.bold,
      lineHeight: Typography.lineHeight.lineHeight16,
      textAlign: Typography.textAlign.centerAlign,
      fontFamily: Typography.fontFamily.inter,
    },

    circleTextActive: {color: theme.activeCircleText},
    circleTextCompleted: {color: theme.completedCircleText},
    circleTextInactive: {color: theme.inactiveCircleText},

    // ── Connector ─────────────────────────────────────────────────────────────
    // Absolutely positioned inside the row. left/width are set by the component
    // from the measured rowWidth.  top:15 = (32−2)/2 centres the 2 px line on
    // the 32 px circle.  zIndex:0 keeps connectors behind circles (zIndex:1).
    connector: {
      position: 'absolute',
      top: 15,
      height: 2,
      zIndex: 0,
    },

    connectorActive: {
      backgroundColor: theme.connectorActive,
    },

    connectorInactive: {
      backgroundColor: theme.connectorInactive,
    },

    // ── Step label ────────────────────────────────────────────────────────────
    // maxWidth is set inline per-label (from computeLabelMaxWidth × 0.75) so
    // text wraps at the optimal word boundary to 2 lines.
    // textAlign:'center' centres the wrapped text under the circle.
    // alignItems:'center' on the parent stepGroup horizontally centres the label.
    label: {
      fontSize: Typography.fontSize.fontSize14,
      fontFamily: Typography.fontFamily.inter,
      textAlign: Typography.textAlign.centerAlign,
      fontWeight: Typography.fontWeight.regular,
      ...Platform.select({
        ios: {lineHeight: Typography.lineHeight.lineHeight20},
        android: {lineHeight: Typography.lineHeight.lineHeight20},
        default: {},
      }),
    },

    labelActive: {
      color: theme.activeLabel,
      fontWeight: Typography.fontWeight.regular,
    },

    labelCompleted: {
      color: theme.completedLabel,
      fontWeight: Typography.fontWeight.medium,
    },

    labelInactive: {
      color: theme.inactiveLabel,
      fontWeight: Typography.fontWeight.regular,
    },
  });
